import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Edit, Trash2, X, Download, Printer } from 'lucide-react';
import { invoiceService, patientService } from '../lib/services';
import { useStore } from '../lib/store';
import { invoiceSchema, invoiceItemSchema, type InvoiceFormData, type InvoiceItemFormData } from '../lib/validation';
import { printInvoice, downloadInvoice } from '../lib/pdf';
import toast from 'react-hot-toast';
import type { Invoice } from '../lib/supabase';

const Billing: React.FC = () => {
  const { invoices, setInvoices, addInvoice, updateInvoice, removeInvoice, patients, setPatients } = useStore();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(null);

  const {
    register: registerInvoice,
    handleSubmit: handleSubmitInvoice,
    reset: resetInvoice,
    formState: { errors: invoiceErrors, isSubmitting: isSubmittingInvoice },
  } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
  });

  const {
    register: registerItem,
    handleSubmit: handleSubmitItem,
    reset: resetItem,
    formState: { errors: itemErrors, isSubmitting: isSubmittingItem },
  } = useForm<InvoiceItemFormData>({
    resolver: zodResolver(invoiceItemSchema),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesData, patientsData] = await Promise.all([
        invoiceService.getAll(),
        patientService.getAll(),
      ]);
      setInvoices(invoicesData);
      setPatients(patientsData);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitInvoice = async (data: InvoiceFormData) => {
    try {
      console.log('Form data received:', data);
      
      const subtotal = 100; // Default subtotal for testing
      const taxAmount = (subtotal * (data.tax_rate || 0)) / 100;
      const discountAmount = 0;
      const total = subtotal + taxAmount - discountAmount;
      const amountPaid = 0;
      const balanceDue = total - amountPaid;

      const invoiceData = {
        patient_id: data.patient_id,
        invoice_number: editingInvoice?.invoice_number || `INV-${Date.now()}`,
        issue_date: new Date().toISOString().split('T')[0],
        due_date: data.due_date,
        subtotal,
        tax_rate: data.tax_rate || 0,
        tax_amount: taxAmount,
        total,
        discount_amount: discountAmount,
        amount_paid: amountPaid,
        balance_due: balanceDue,
        status: 'draft' as const,
        notes: data.notes
      };

      console.log('Invoice data to save:', invoiceData);

      if (editingInvoice) {
        const updated = await invoiceService.update(editingInvoice.id, invoiceData);
        updateInvoice(editingInvoice.id, updated);
        toast.success('Invoice updated successfully');
      } else {
        const created = await invoiceService.create(invoiceData);
        addInvoice(created);
        toast.success('Invoice created successfully');
      }
      setShowModal(false);
      setEditingInvoice(null);
      resetInvoice();
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      toast.error(error.message || 'Operation failed');
    }
  };

  const onSubmitItem = async (data: InvoiceItemFormData) => {
    try {
      if (currentInvoiceId) {
        await invoiceService.addItem(currentInvoiceId, data);
        toast.success('Item added successfully');
        // Reload invoice to get updated totals
        const updated = await invoiceService.getById(currentInvoiceId);
        updateInvoice(currentInvoiceId, updated);
        setShowItemModal(false);
        resetItem();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to add item');
    }
  };

  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    resetInvoice({
      patient_id: invoice.patient_id,
      due_date: invoice.due_date || '',
      tax_rate: invoice.tax_rate,
      notes: invoice.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await invoiceService.delete(id);
      removeInvoice(id);
      toast.success('Invoice deleted successfully');
      setDeleteConfirm(null);
    } catch (error: any) {
      toast.error(error.message || 'Delete failed');
    }
  };

  const handlePrint = (invoice: Invoice) => {
    printInvoice(invoice);
  };

  const handleDownload = (invoice: Invoice) => {
    downloadInvoice(invoice);
  };

  const openAddModal = () => {
    setEditingInvoice(null);
    resetInvoice({ tax_rate: 10 });
    setShowModal(true);
  };

  const openItemModal = (invoiceId: string) => {
    setCurrentInvoiceId(invoiceId);
    resetItem();
    setShowItemModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing</h1>
            <p className="text-gray-600">Handle billing and payment processing.</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            <span>New Invoice</span>
          </button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.patient?.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(invoice.issue_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${invoice.total.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openItemModal(invoice.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Add Items"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handlePrint(invoice)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Print"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(invoice)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(invoice.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingInvoice ? 'Edit Invoice' : 'New Invoice'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitInvoice(onSubmitInvoice)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Patient *</label>
                <select
                  {...registerInvoice('patient_id')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name}
                    </option>
                  ))}
                </select>
                {invoiceErrors.patient_id && <p className="mt-1 text-sm text-red-600">{invoiceErrors.patient_id.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Due Date</label>
                <input
                  {...registerInvoice('due_date')}
                  type="date"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Tax Rate (%)</label>
                <input
                  {...registerInvoice('tax_rate', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {invoiceErrors.tax_rate && <p className="mt-1 text-sm text-red-600">{invoiceErrors.tax_rate.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  {...registerInvoice('notes')}
                  rows={3}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingInvoice}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {isSubmittingInvoice ? 'Saving...' : editingInvoice ? 'Update' : 'Save Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showItemModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Invoice Item</h3>
              <button
                onClick={() => setShowItemModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitItem(onSubmitItem)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Description *</label>
                <input
                  {...registerItem('description')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {itemErrors.description && <p className="mt-1 text-sm text-red-600">{itemErrors.description.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                  <input
                    {...registerItem('quantity', { valueAsNumber: true })}
                    type="number"
                    min="1"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {itemErrors.quantity && <p className="mt-1 text-sm text-red-600">{itemErrors.quantity.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Unit Price *</label>
                  <input
                    {...registerItem('unit_price', { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    min="0"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {itemErrors.unit_price && <p className="mt-1 text-sm text-red-600">{itemErrors.unit_price.message}</p>}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowItemModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingItem}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {isSubmittingItem ? 'Adding...' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this invoice? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
